/**
 * Peças da Fase 4 usadas pelo atleta e pela equipe: evolução (gráficos com filtros), recordes, provas,
 * metas, alertas explicáveis e bem-estar com consentimento.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError } from "../api/client";
import type { Alert, AthleteDashboard, Consent, Goal, GoalChange, GoalMetric, PersonalRecord, Race, RaceStatus, Records, Wellness } from "../api/types";
import { formatDate, formatDateTime, formatDuration, formatPace, metersToKm, paceShort, parseDuration } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import { Link } from "../router/router";
import { BarChart, ChartFrame, INTENSITY_COLOR, INTENSITY_LABEL, StackedBars, TimeChart } from "./charts";
import { Badge, Button, EmptyState, FormError, Loading, Modal, SelectField, TextArea, TextField, useToast } from "./ui";
import { LOCALE, t, tn } from "../i18n";

const kmNum = (m: number) => (m / 1000).toLocaleString(LOCALE, { maximumFractionDigits: 1 });
const shortDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const minutes = (s: number) => `${Math.round(s / 60)} min`;

export const SOURCE_LABEL: Record<PersonalRecord["source"], string> = {
  activity_effort: t("trecho de atividade"),
  activity: t("atividade"),
  race: t("prova"),
};
export const RACE_STATUS: Record<RaceStatus, { label: string; tone: "ok" | "warn" | "off" | "danger" }> = {
  planned: { label: t("Planejada"), tone: "off" },
  completed: { label: t("Concluída"), tone: "ok" },
  did_not_start: { label: t("Não largou"), tone: "warn" },
  did_not_finish: { label: t("Não concluiu"), tone: "danger" },
};
export const GOAL_METRIC: Record<GoalMetric, { label: string; unit: "m" | "sessions"; period: string }> = {
  weekly_distance: { label: t("Distância por semana"), unit: "m", period: t("nesta semana") },
  monthly_distance: { label: t("Distância por mês"), unit: "m", period: t("neste mês") },
  weekly_sessions: { label: t("Corridas por semana"), unit: "sessions", period: t("nesta semana") },
  period_distance: { label: t("Distância no período"), unit: "m", period: t("no período") },
};

/** Fração 0–1 → "67 %". */
export function rate(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : `${Math.round(v * 100)} %`;
}

export function goalValue(metric: GoalMetric, v: number): string {
  return GOAL_METRIC[metric].unit === "m" ? metersToKm(v) : tn("{n} corrida", "{n} corridas", v);
}

export function Kpi({ label, value, sub, meter }: { label: string; value: string; sub?: string; meter?: number | null }) {
  return (
    <div className="kpi">
      <dt>{label}</dt>
      <dd>
        <span className="kpi-value">{value}</span>
        {sub && <span className="kpi-sub">{sub}</span>}
        {meter != null && (
          <span className={`meter${meter >= 1 ? " done" : ""}`} aria-hidden>
            <span style={{ width: `${Math.min(100, Math.round(meter * 100))}%` }} />
          </span>
        )}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------- recordes

export function RecordsGrid({ records, empty }: { records: PersonalRecord[]; empty?: string }) {
  if (records.length === 0)
    return (
      <div className="panel">
        <EmptyState title={t("Nenhum recorde ainda")}>{empty ?? t("Os recordes aparecem quando você importa corridas de 1 km ou mais com o relógio.")}</EmptyState>
      </div>
    );
  return (
    <dl className="records">
      {records.map((r) => (
        <Kpi key={r.distance_key} label={r.label} value={formatDuration(r.time_s)} sub={`${formatPace(r.pace_s_per_km)} · ${formatDate(r.achieved_on)} · ${SOURCE_LABEL[r.source]}`} />
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------- alertas

export function AlertsList({
  alerts,
  orgId,
  athleteId,
  onChanged,
  showAthlete,
  athleteHref,
}: {
  alerts: Alert[];
  orgId: string;
  athleteId?: string;
  onChanged?: () => void;
  showAthlete?: boolean;
  athleteHref?: (id: string) => string;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  async function ack(a: Alert) {
    const id = a.athlete_id ?? athleteId;
    if (!id) return;
    setBusy(a.key);
    try {
      await api.post(`/orgs/${orgId}/athletes/${id}/alerts/ack`, { key: a.key });
      toast(t("Alerta marcado como visto."));
      onChanged?.();
    } catch (e) {
      toast((e as ApiError).message, "error");
    } finally {
      setBusy(null);
    }
  }
  if (alerts.length === 0)
    return (
      <div className="panel">
        <EmptyState title={t("Nenhum alerta aberto")}>{t("As regras são avaliadas a cada consulta, a partir dos dados gravados.")}</EmptyState>
      </div>
    );
  return (
    <div className="panel">
      <ul className="list-plain">
        {alerts.map((a) => (
          <li key={`${a.athlete_id ?? ""}${a.key}`} className="alert-item">
            <span className="sev">
              <Badge tone={a.severity === "attention" ? "warn" : "off"}>{a.severity === "attention" ? t("Atenção") : t("Info")}</Badge>
            </span>
            <div>
              <strong>{a.title}</strong>
              {showAthlete && a.athlete_name && (
                <>
                  {" — "}
                  {athleteHref && a.athlete_id ? <Link to={athleteHref(a.athlete_id)}>{a.athlete_name}</Link> : a.athlete_name}
                </>
              )}
              <div>{a.message}</div>
            </div>
            <div>
              {a.acknowledged_at ? (
                <span className="muted" style={{ fontSize: "var(--step--1)" }}>
                  {" "}{t("Visto em {acknowledged_at}", { acknowledged_at: formatDateTime(a.acknowledged_at) })}
                </span>
              ) : (
                <Button size="sm" busy={busy === a.key} onClick={() => ack(a)}>
                  {t("Marcar como visto")}
                </Button>
              )}
            </div>
            <div className="why">
              <b>{t("Origem:")}</b> {a.source}. <b>{t("Regra:")}</b> {a.rule_text} <b>{t("Data:")}</b> {formatDate(a.triggered_on)}.
            </div>
          </li>
        ))}
      </ul>
      <p className="alert-note">
        {t("Alertas são apoio à decisão do treinador: sinalizam mudanças nos dados, não diagnosticam lesão nem risco clínico.")}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------- provas

function RaceModal({
  open,
  race,
  createPath,
  onClose,
  onSaved,
  orgId,
}: {
  open: boolean;
  race: Race | null;
  createPath: string;
  onClose: () => void;
  onSaved: () => void;
  orgId: string;
}) {
  const [f, setF] = useState({ name: "", race_date: "", location: "", distance_km: "", priority: "B", target: "", status: "planned", result: "", result_source: "athlete", notes: "" });
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setError(null);
    setF(
      race
        ? {
            name: race.name, race_date: race.race_date, location: race.location ?? "", distance_km: String(race.distance_m / 1000).replace(".", ","),
            priority: race.priority, target: race.target_time_s ? formatDuration(race.target_time_s) : "", status: race.status,
            result: race.result_time_s ? formatDuration(race.result_time_s) : "", result_source: race.result_source ?? "athlete", notes: race.notes ?? "",
          }
        : { name: "", race_date: "", location: "", distance_km: "", priority: "B", target: "", status: "planned", result: "", result_source: "athlete", notes: "" },
    );
  }, [open, race]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const dist = Math.round(parseFloat(f.distance_km.replace(",", ".")) * 1000);
    const target = f.target ? parseDuration(f.target) : null;
    const result = f.result ? parseDuration(f.result) : null;
    if (f.target && target === null) {
      setError(new ApiError(422, "validation_error", t("Tempo-alvo inválido. Use h:mm:ss ou mm:ss."), []));
      setBusy(false);
      return;
    }
    const body = {
      name: f.name, race_date: f.race_date, location: f.location || null, distance_m: Number.isFinite(dist) ? dist : 0,
      priority: f.priority, target_time_s: target, status: f.status,
      result_time_s: f.status === "completed" ? result : null, result_source: f.status === "completed" ? f.result_source : null, notes: f.notes || null,
    };
    try {
      if (race) await api.patch(`/orgs/${orgId}/races/${race.id}`, body);
      else await api.post(createPath, body);
      onSaved();
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }
  const fe = error?.fieldErrors() ?? {};
  return (
    <Modal
      open={open}
      title={race ? t("Editar prova") : t("Nova prova")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("Cancelar")}</Button>
          <Button variant="primary" type="submit" form="race-form" busy={busy}>
            {t("Salvar")}
          </Button>
        </>
      }
    >
      <form id="race-form" className="stack" onSubmit={submit} noValidate>
        <FormError error={error && !Object.keys(fe).length ? error : null} />
        <TextField label={t("Nome da prova")} value={f.name} onChange={set("name")} required error={fe.name} />
        <div className="form-grid">
          <TextField label={t("Data")} type="date" value={f.race_date} onChange={set("race_date")} required error={fe.race_date} />
          <TextField label={t("Distância (km)")} inputMode="decimal" value={f.distance_km} onChange={set("distance_km")} required hint={t("Ex.: 21,097")} error={fe.distance_m} />
          <TextField label={t("Local")} value={f.location} onChange={set("location")} error={fe.location} />
          <SelectField label={t("Prioridade")} value={f.priority} onChange={set("priority")} hint={t("A = prova-alvo do ciclo")}>
            <option value="A">{t("A — principal")}</option>
            <option value="B">B</option>
            <option value="C">{t("C — treino/participação")}</option>
          </SelectField>
          <TextField label={t("Objetivo de tempo")} value={f.target} onChange={set("target")} placeholder="1:45:00" hint="h:mm:ss" error={fe.target_time_s} />
          <SelectField label={t("Situação")} value={f.status} onChange={set("status")}>
            {Object.entries(RACE_STATUS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </SelectField>
          {f.status === "completed" && (
            <>
              <TextField label={t("Tempo final")} value={f.result} onChange={set("result")} placeholder="1:42:10" required error={fe.result_time_s} />
              <SelectField label={t("Fonte do resultado")} value={f.result_source} onChange={set("result_source")}>
                <option value="official">{t("Resultado oficial")}</option>
                <option value="athlete">{t("Informado pelo atleta")}</option>
              </SelectField>
            </>
          )}
        </div>
        <TextArea label={t("Observações")} value={f.notes} onChange={set("notes")} rows={2} />
      </form>
    </Modal>
  );
}

export function RacesPanel({ orgId, athleteId, self }: { orgId: string; athleteId?: string; self: boolean }) {
  const toast = useToast();
  const listPath = self ? `/orgs/${orgId}/me/races` : `/orgs/${orgId}/athletes/${athleteId}/races`;
  const races = useAsync(() => api.get<Race[]>(listPath), [listPath]);
  const [editing, setEditing] = useState<Race | null | "new">(null);
  const today = new Date().toISOString().slice(0, 10);
  const list = races.data ?? [];
  const upcoming = list.filter((r) => r.race_date >= today && r.status === "planned").sort((a, b) => a.race_date.localeCompare(b.race_date));
  const past = list.filter((r) => !upcoming.includes(r));
  async function remove(r: Race) {
    if (!window.confirm(t("Excluir a prova “{name}”?", { name: r.name }))) return;
    try {
      await api.delete(`/orgs/${orgId}/races/${r.id}`);
      toast(t("Prova excluída."));
      races.reload();
    } catch (e) {
      toast((e as ApiError).message, "error");
    }
  }
  const row = (r: Race) => (
    <li key={r.id} className="row-between">
      <div>
        <span className={`prio ${r.priority}`} title={t("Prioridade {priority}", { priority: r.priority })}>
          {r.priority}
        </span>{" "}
        <strong>{r.name}</strong>
        <div className="muted" style={{ fontSize: "var(--step--1)" }}>
          <span className="race-date">{formatDate(r.race_date)}</span> · {metersToKm(r.distance_m)}
          {r.location && ` · ${r.location}`}
          {r.target_time_s && ` · objetivo ${formatDuration(r.target_time_s)} (${formatPace(r.target_pace_s_per_km)})`}
        </div>
      </div>
      <div className="row-between" style={{ gap: 8 }}>
        {r.status === "completed" && r.result_time_s ? (
          <span className="figs">
            <strong>{formatDuration(r.result_time_s)}</strong> <span className="muted">{r.result_source === "official" ? "oficial" : "informado"}</span>
          </span>
        ) : (
          <Badge tone={RACE_STATUS[r.status].tone}>{RACE_STATUS[r.status].label}</Badge>
        )}
        <Button size="sm" onClick={() => setEditing(r)}>
          {r.status === "planned" && r.race_date <= today ? t("Registrar resultado") : t("Editar")}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => remove(r)} aria-label={t("Excluir {name}", { name: r.name })}>
          {t("Excluir")}
        </Button>
      </div>
    </li>
  );
  return (
    <section className="section" aria-labelledby="provas">
      <div className="row-between">
        <h2 id="provas">{t("Provas")}</h2>
        <Button variant="primary" size="sm" onClick={() => setEditing("new")}>
          {t("Nova prova")}
        </Button>
      </div>
      {races.loading && <Loading />}
      {races.data && list.length === 0 && (
        <div className="panel" style={{ marginTop: 10 }}>
          <EmptyState title={t("Nenhuma prova cadastrada")}>{t("Cadastre as próximas provas com o objetivo de tempo; depois registre o resultado.")}</EmptyState>
        </div>
      )}
      {upcoming.length > 0 && (
        <div className="panel" style={{ marginTop: 10 }}>
          <div className="panel-head">
            <h3>{t("Próximas")}</h3>
          </div>
          <ul className="list-plain">{upcoming.map(row)}</ul>
        </div>
      )}
      {past.length > 0 && (
        <div className="panel" style={{ marginTop: 10 }}>
          <div className="panel-head">
            <h3>{t("Histórico de participação")}</h3>
          </div>
          <ul className="list-plain">{past.map(row)}</ul>
        </div>
      )}
      <RaceModal
        open={editing !== null}
        race={editing === "new" ? null : editing}
        createPath={listPath}
        orgId={orgId}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          toast(t("Prova salva."));
          races.reload();
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------- metas

function GoalModal({ open, goal, orgId, athleteId, onClose, onSaved }: { open: boolean; goal: Goal | null; orgId: string; athleteId: string; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ title: "", metric: "weekly_distance" as GoalMetric, target: "", start_date: "", end_date: "", status: "active", notes: "" });
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setError(null);
    const today = new Date().toISOString().slice(0, 10);
    setF(
      goal
        ? { title: goal.title, metric: goal.metric, target: GOAL_METRIC[goal.metric].unit === "m" ? String(goal.target_value / 1000).replace(".", ",") : String(goal.target_value), start_date: goal.start_date, end_date: goal.end_date ?? "", status: goal.status, notes: goal.notes ?? "" }
        : { title: "", metric: "weekly_distance", target: "", start_date: today, end_date: "", status: "active", notes: "" },
    );
  }, [open, goal]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const isDist = GOAL_METRIC[f.metric].unit === "m";
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const n = parseFloat(f.target.replace(",", "."));
    const target_value = Number.isFinite(n) ? Math.round(isDist ? n * 1000 : n) : 0;
    try {
      if (goal) await api.patch(`/orgs/${orgId}/goals/${goal.id}`, { title: f.title, target_value, end_date: f.end_date || null, status: f.status, notes: f.notes || null });
      else
        await api.post(`/orgs/${orgId}/goals`, { athlete_id: athleteId, title: f.title, metric: f.metric, target_value, start_date: f.start_date, end_date: f.end_date || null, notes: f.notes || null });
      onSaved();
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }
  const fe = error?.fieldErrors() ?? {};
  return (
    <Modal
      open={open}
      title={goal ? t("Editar meta") : t("Nova meta")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("Cancelar")}</Button>
          <Button variant="primary" type="submit" form="goal-form" busy={busy}>
            {t("Salvar")}
          </Button>
        </>
      }
    >
      <form id="goal-form" className="stack" onSubmit={submit} noValidate>
        <FormError error={error && !Object.keys(fe).length ? error : null} />
        <TextField label={t("Título")} value={f.title} onChange={set("title")} required error={fe.title} placeholder={t("Ex.: 30 km por semana")} />
        <div className="form-grid">
          <SelectField label={t("Tipo de meta")} value={f.metric} onChange={set("metric")} disabled={!!goal}>
            {Object.entries(GOAL_METRIC).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </SelectField>
          <TextField label={isDist ? t("Alvo (km)") : t("Alvo (corridas)")} inputMode="decimal" value={f.target} onChange={set("target")} required error={fe.target_value} />
          <TextField label={t("Início")} type="date" value={f.start_date} onChange={set("start_date")} disabled={!!goal} error={fe.start_date} />
          <TextField label={t("Fim (opcional)")} type="date" value={f.end_date} onChange={set("end_date")} error={fe.end_date} hint={f.metric === "period_distance" ? t("Obrigatório para este tipo") : undefined} />
          {goal && (
            <SelectField label={t("Situação")} value={f.status} onChange={set("status")}>
              <option value="active">{t("Ativa")}</option>
              <option value="achieved">{t("Cumprida")}</option>
              <option value="cancelled">{t("Cancelada")}</option>
            </SelectField>
          )}
        </div>
        <TextArea label={t("Observações")} value={f.notes} onChange={set("notes")} rows={2} />
        {goal && <p className="muted" style={{ fontSize: "var(--step--1)" }}>{t("A alteração fica registrada no histórico da meta.")}</p>}
      </form>
    </Modal>
  );
}

function GoalHistory({ orgId, goal, onClose }: { orgId: string; goal: Goal | null; onClose: () => void }) {
  const hist = useAsync(() => (goal ? api.get<GoalChange[]>(`/orgs/${orgId}/goals/${goal.id}/history`) : Promise.resolve([])), [goal?.id]);
  return (
    <Modal open={!!goal} title={t("Histórico — {title}", { title: goal?.title ?? "" })} onClose={onClose} footer={<Button onClick={onClose}>{t("Fechar")}</Button>}>
      {hist.loading && <Loading />}
      <ul className="list-plain">
        {(hist.data ?? []).map((h, i) => (
          <li key={i}>
            <strong>{h.action === "created" ? t("Criada") : h.action === "status" ? t("Situação alterada") : t("Alterada")}</strong>{" "}
            {h.changed_by_name
              ? t("em {date} por {name}", { date: formatDateTime(h.changed_at), name: h.changed_by_name })
              : t("em {date}", { date: formatDateTime(h.changed_at) })}
            {h.action !== "created" && (
              <div className="muted" style={{ fontSize: "var(--step--1)" }}>
                {Object.entries(h.changes)
                  .map(([k, v]) => {
                    const c = v as { de: string | null; para: string | null };
                    return `${k}: ${c.de ?? "—"} → ${c.para ?? "—"}`;
                  })
                  .join("; ")}
              </div>
            )}
          </li>
        ))}
      </ul>
    </Modal>
  );
}

export function GoalsPanel({ orgId, goals, athleteId, canManage, onChanged }: { orgId: string; goals: Goal[]; athleteId: string; canManage: boolean; onChanged: () => void }) {
  const toast = useToast();
  const [editing, setEditing] = useState<Goal | null | "new">(null);
  const [history, setHistory] = useState<Goal | null>(null);
  return (
    <section className="section" aria-labelledby="metas">
      <div className="row-between">
        <h2 id="metas">{t("Metas")}</h2>
        {canManage && (
          <Button variant="primary" size="sm" onClick={() => setEditing("new")}>
            {t("Nova meta")}
          </Button>
        )}
      </div>
      {goals.length === 0 ? (
        <div className="panel" style={{ marginTop: 10 }}>
          <EmptyState title={t("Nenhuma meta ativa")}>{canManage ? t("Crie metas de volume ou frequência; o progresso é calculado das atividades.") : t("Quando o treinador definir metas para você ou para o seu grupo, elas aparecem aqui.")}</EmptyState>
        </div>
      ) : (
        <div className="kpis" style={{ marginTop: 10 }}>
          {goals.map((g) => {
            const p = g.progress?.athletes.find((x) => x.athlete_id === athleteId) ?? g.progress?.athletes[0];
            return (
              <div className="kpi" key={g.id}>
                <dt>
                  {g.title}
                  {g.group_name && !g.title.includes(g.group_name) && <span className="muted"> · {t("grupo {name}", { name: g.group_name })}</span>}
                </dt>
                <dd>
                  {p ? (
                    <>
                      <span className="kpi-value">{goalValue(g.metric, p.value)}</span>
                      <span className="kpi-sub">
                        {t("de {target} {period} ({pct} %)", { target: goalValue(g.metric, g.target_value), period: GOAL_METRIC[g.metric].period, pct: Math.round(p.pct * 100) })}
                      </span>
                      <span className={`meter${p.pct >= 1 ? " done" : ""}`} aria-hidden>
                        <span style={{ width: `${Math.min(100, Math.round(p.pct * 100))}%` }} />
                      </span>
                    </>
                  ) : (
                    <span className="kpi-sub">{g.status === "achieved" ? t("Cumprida") : t("Encerrada")}</span>
                  )}
                  {g.progress && g.progress.history.length > 1 && (
                    <span className="kpi-sub">
                      {t("Semanas cumpridas: {a} de {b}", { a: g.progress.history.filter((h) => h.achieved).length, b: g.progress.history.length })}
                    </span>
                  )}
                  <span className="row-between" style={{ marginTop: 8, justifyContent: "flex-start" }}>
                    <Button size="sm" variant="ghost" onClick={() => setHistory(g)}>
                      {t("Histórico")}
                    </Button>
                    {canManage && g.athlete_id && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(g)}>
                        {t("Editar")}
                      </Button>
                    )}
                  </span>
                </dd>
              </div>
            );
          })}
        </div>
      )}
      {canManage && (
        <GoalModal
          open={editing !== null}
          goal={editing === "new" ? null : editing}
          orgId={orgId}
          athleteId={athleteId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast(t("Meta salva."));
            onChanged();
          }}
        />
      )}
      <GoalHistory orgId={orgId} goal={history} onClose={() => setHistory(null)} />
    </section>
  );
}

// ---------------------------------------------------------------------------- bem-estar

function Scale({ label, value, onChange, low, high }: { label: string; value: number | null; onChange: (v: number | null) => void; low: string; high: string }) {
  return (
    <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="label">{label}</legend>
      <div className="scale">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" aria-pressed={value === n} onClick={() => onChange(value === n ? null : n)} aria-label={`${label}: ${n}`}>
            {n}
          </button>
        ))}
      </div>
      <div className="scale-hint">
        <span>1 {low}</span>
        <span>5 {high}</span>
      </div>
    </fieldset>
  );
}

export function ConsentBox({ orgId, consent, onChange }: { orgId: string; consent: Consent; onChange: (c: Consent) => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [deleteHistory, setDeleteHistory] = useState(false);
  async function grant() {
    setBusy(true);
    try {
      onChange(await api.post<Consent>(`/orgs/${orgId}/me/consents/wellness`));
      toast(t("Consentimento registrado."));
    } catch (e) {
      toast((e as ApiError).message, "error");
    } finally {
      setBusy(false);
    }
  }
  async function revoke() {
    setBusy(true);
    try {
      onChange(await api.post<Consent>(`/orgs/${orgId}/me/consents/wellness/revoke`, { delete_history: deleteHistory }));
      setRevoking(false);
      toast(deleteHistory ? t("Consentimento revogado e histórico excluído.") : t("Consentimento revogado."));
    } catch (e) {
      toast((e as ApiError).message, "error");
    } finally {
      setBusy(false);
    }
  }
  if (!consent.granted)
    return (
      <div className="stack">
        <p>{t("Para registrar sono, fadiga e dores, precisamos do seu consentimento — são dados de saúde.")}</p>
        <div className="consent-text">{consent.text}</div>
        <div>
          <Button variant="primary" busy={busy} onClick={grant}>
            {t("Concordo e quero registrar")}
          </Button>
        </div>
      </div>
    );
  return (
    <div className="stack">
      <p className="muted" style={{ fontSize: "var(--step--1)" }}>
        {t("Consentimento dado em {date} (versão {version}).", { date: formatDateTime(consent.granted_at), version: consent.text_version })}{" "}
        <button type="button" className="link-btn" onClick={() => setRevoking(true)}>
          {t("Revogar")}
        </button>
      </p>
      <Modal
        open={revoking}
        title={t("Revogar consentimento")}
        onClose={() => setRevoking(false)}
        footer={
          <>
            <Button onClick={() => setRevoking(false)}>{t("Cancelar")}</Button>
            <Button variant="danger" busy={busy} onClick={revoke}>
              {t("Revogar")}
            </Button>
          </>
        }
      >
        <div className="stack">
          <p>{t("A equipe deixa de ver seus check-ins e você não poderá registrar novos até consentir de novo.")}</p>
          <label className="checkbox">
            <input type="checkbox" checked={deleteHistory} onChange={(e) => setDeleteHistory(e.target.checked)} /> {" "}{t("Excluir também todo o histórico de check-ins")}
          </label>
        </div>
      </Modal>
    </div>
  );
}

const EMPTY_W = { sleep_hours: "", sleep_quality: null as number | null, fatigue: null as number | null, soreness: null as number | null, stress: null as number | null, pain: false, pain_area: "", pain_intensity: "", note: "" };

export function WellnessCheckin({ orgId, today, onSaved }: { orgId: string; today: Wellness | null; onSaved: () => void }) {
  const toast = useToast();
  const consentState = useAsync(() => api.get<Consent>(`/orgs/${orgId}/me/consents/wellness`), [orgId]);
  const [consent, setConsent] = useState<Consent | null>(null);
  const [f, setF] = useState(EMPTY_W);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => setConsent(consentState.data), [consentState.data]);
  useEffect(() => {
    setF(
      today
        ? { sleep_hours: today.sleep_hours != null ? today.sleep_hours.toLocaleString(LOCALE) : "", sleep_quality: today.sleep_quality, fatigue: today.fatigue, soreness: today.soreness, stress: today.stress, pain: today.pain, pain_area: today.pain_area ?? "", pain_intensity: today.pain_intensity != null ? String(today.pain_intensity) : "", note: today.note ?? "" }
        : EMPTY_W,
    );
  }, [today]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const sh = f.sleep_hours ? parseFloat(f.sleep_hours.replace(",", ".")) : null;
    try {
      await api.put(`/orgs/${orgId}/me/wellness`, {
        sleep_hours: sh, sleep_quality: f.sleep_quality, fatigue: f.fatigue, soreness: f.soreness, stress: f.stress,
        pain: f.pain, pain_area: f.pain ? f.pain_area : null, pain_intensity: f.pain && f.pain_intensity !== "" ? Number(f.pain_intensity) : null, note: f.note || null,
      });
      toast(t("Check-in salvo."));
      setOpen(false);
      onSaved();
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }
  const fe = error?.fieldErrors() ?? {};
  if (!consent) return consentState.loading ? <Loading /> : null;
  return (
    <section className="panel section" aria-labelledby="bem-estar">
      <div className="panel-head">
        <h2 id="bem-estar">{t("Como você está hoje?")}</h2>
        {consent.granted && !open && (
          <Button size="sm" variant={today ? "secondary" : "primary"} onClick={() => setOpen(true)}>
            {today ? t("Corrigir check-in") : t("Fazer check-in")}
          </Button>
        )}
      </div>
      <div className="panel-body stack">
        {consent.granted && today && !open && (
          <p>
            {t("Check-in de hoje registrado")}
            {today.fatigue && t(" · fadiga {v}/5", { v: today.fatigue })}
            {today.sleep_hours != null && t(" · {v} h de sono", { v: today.sleep_hours.toLocaleString(LOCALE) })}
            {today.pain && ` · dor: ${today.pain_area}`}.
          </p>
        )}
        {consent.granted && !today && !open && <p className="muted">{t("Leva menos de um minuto e ajuda o treinador a ajustar a carga.")}</p>}
        {consent.granted && open && (
          <form className="stack" onSubmit={submit} noValidate>
            <FormError error={error && !Object.keys(fe).length ? error : null} />
            <div className="wellness-grid">
              <TextField label={t("Horas de sono")} inputMode="decimal" value={f.sleep_hours} onChange={(e) => setF({ ...f, sleep_hours: e.target.value })} placeholder={(7.5).toLocaleString(LOCALE)} error={fe.sleep_hours} />
              <Scale label={t("Qualidade do sono")} value={f.sleep_quality} onChange={(v) => setF({ ...f, sleep_quality: v })} low={t("ótima")} high={t("péssima")} />
              <Scale label={t("Fadiga")} value={f.fatigue} onChange={(v) => setF({ ...f, fatigue: v })} low={t("descansado")} high={t("exausto")} />
              <Scale label={t("Dor muscular")} value={f.soreness} onChange={(v) => setF({ ...f, soreness: v })} low={t("nenhuma")} high={t("muita")} />
              <Scale label={t("Estresse")} value={f.stress} onChange={(v) => setF({ ...f, stress: v })} low={t("tranquilo")} high={t("muito")} />
            </div>
            <label className="checkbox">
              <input type="checkbox" checked={f.pain} onChange={(e) => setF({ ...f, pain: e.target.checked })} /> {" "}{t("Estou com alguma dor ou desconforto")}
            </label>
            {f.pain && (
              <div className="form-grid">
                <TextField label={t("Onde?")} value={f.pain_area} onChange={(e) => setF({ ...f, pain_area: e.target.value })} placeholder={t("Ex.: joelho direito")} required error={fe.pain_area} />
                <TextField label={t("Intensidade (0 a 10)")} type="number" min={0} max={10} value={f.pain_intensity} onChange={(e) => setF({ ...f, pain_intensity: e.target.value })} error={fe.pain_intensity} />
              </div>
            )}
            <TextArea label={t("Observação (opcional)")} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} rows={2} />
            <div className="row-between" style={{ justifyContent: "flex-start" }}>
              <Button variant="primary" type="submit" busy={busy}>
                {t("Salvar check-in")}
              </Button>
              <Button onClick={() => setOpen(false)}>{t("Cancelar")}</Button>
            </div>
            <p className="muted" style={{ fontSize: "var(--step--1)" }}>
              {t("Não substitui avaliação médica. Em caso de dor persistente, procure um profissional de saúde.")}
            </p>
          </form>
        )}
        <ConsentBox orgId={orgId} consent={consent} onChange={setConsent} />
      </div>
    </section>
  );
}

export function WellnessStaff({ orgId, athleteId }: { orgId: string; athleteId: string }) {
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - 27 * 86_400_000).toISOString().slice(0, 10);
  const st = useAsync(() => api.get<{ consent: boolean; entries: Wellness[] }>(`/orgs/${orgId}/athletes/${athleteId}/wellness`, { from, to }), [orgId, athleteId]);
  return (
    <section className="section" aria-labelledby="bem-estar-equipe">
      <h2 id="bem-estar-equipe">{t("Bem-estar (últimos 28 dias)")}</h2>
      {st.loading && <Loading />}
      {st.data && !st.data.consent && (
        <div className="panel">
          <EmptyState title={t("Sem consentimento do atleta")}>{t("Os check-ins de bem-estar só ficam visíveis para a equipe depois que o atleta concorda com o termo no app.")}</EmptyState>
        </div>
      )}
      {st.data && st.data.consent && st.data.entries.length === 0 && (
        <div className="panel">
          <EmptyState title={t("Nenhum check-in no período")} />
        </div>
      )}
      {st.data && st.data.consent && st.data.entries.length > 0 && (
        <div className="table-wrap">
          <table className="data responsive">
            <thead>
              <tr>
                <th>{t("Data")}</th>
                <th>{t("Sono")}</th>
                <th>{t("Qualidade")}</th>
                <th>{t("Fadiga")}</th>
                <th>{t("Dor muscular")}</th>
                <th>{t("Estresse")}</th>
                <th>{t("Dor / desconforto")}</th>
              </tr>
            </thead>
            <tbody>
              {st.data.entries.map((w) => (
                <tr key={w.entry_date}>
                  <td data-label={t("Data")}>{formatDate(w.entry_date)}</td>
                  <td data-label={t("Sono")} className="num">{w.sleep_hours != null ? t("{v} h", { v: w.sleep_hours.toLocaleString(LOCALE) }) : "—"}</td>
                  <td data-label={t("Qualidade")} className="num">{w.sleep_quality ?? "—"}</td>
                  <td data-label={t("Fadiga")} className="num">{w.fatigue ?? "—"}</td>
                  <td data-label={t("Dor muscular")} className="num">{w.soreness ?? "—"}</td>
                  <td data-label={t("Estresse")} className="num">{w.stress ?? "—"}</td>
                  <td data-label={t("Dor")}>{w.pain ? `${w.pain_area}${w.pain_intensity != null ? ` (${w.pain_intensity}/10)` : ""}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted" style={{ fontSize: "var(--step--1)", marginTop: 6 }}>
            {t("Escalas de 1 (melhor) a 5 (pior). Dados sensíveis compartilhados com consentimento; cada consulta fica registrada na auditoria.")}
          </p>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------- evolução (gráficos + filtros)

export interface EvolutionFilters {
  weeks: number;
  sport: "run" | "strength" | "all";   // "strength" = só treino de força (não consulta o painel de corrida)
}

export const WEEK_PRESETS = [4, 12, 26, 52];

export function PeriodFilters({ value, onChange, bucket, onBucket }: { value: EvolutionFilters; onChange: (v: EvolutionFilters) => void; bucket: string; onBucket: (b: string) => void }) {
  return (
    <div className="filters" role="group" aria-label={t("Filtros")}>
      <div>
        <span className="label-sm">{t("Período")}</span>
        <div className="segmented">
          {WEEK_PRESETS.map((w) => (
            <button key={w} type="button" aria-pressed={value.weeks === w} onClick={() => onChange({ ...value, weeks: w })}>
              {w === 52 ? t("1 ano") : t("{n} semanas", { n: w })}
            </button>
          ))}
        </div>
      </div>
      <div>
        <span className="label-sm">{t("Atividades")}</span>
        <div className="segmented">
          <button type="button" aria-pressed={value.sport === "run"} onClick={() => onChange({ ...value, sport: "run" })}>
            {t("Corrida")}
          </button>
          <button type="button" aria-pressed={value.sport === "strength"} onClick={() => onChange({ ...value, sport: "strength" })}>
            {t("Força")}
          </button>
          <button type="button" aria-pressed={value.sport === "all"} onClick={() => onChange({ ...value, sport: "all" })}>
            {t("Todas")}
          </button>
        </div>
      </div>
      {value.sport !== "strength" && (
      <div>
        <span className="label-sm">{t("Distância (ritmo e FC)")}</span>
        <div className="segmented">
          {[["", t("Todas")], ["3-7", "3–7 km"], ["7-12", "7–12 km"], ["12-18", "12–18 km"], ["18+", "18 km+"]].map(([k, l]) => (
            <button key={k} type="button" aria-pressed={bucket === k} onClick={() => onBucket(k)}>
              {l}
            </button>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}

export function EvolutionCharts({ dash, bucket, records }: { dash: AthleteDashboard; bucket: string; records: Records | null }) {
  const { summary, period } = dash;
  const weeks = summary.weeks;
  const every = weeks.length > 26 ? 4 : weeks.length > 12 ? 2 : 1;
  const runs = summary.points.filter((p) => (!bucket || p.bucket === bucket) && p.sport !== "walk" && p.sport !== "bike");
  const paced = runs.filter((p) => p.pace_s_per_km);
  const eff = runs.filter((p) => p.efficiency);
  const [recKey, setRecKey] = useState<string>("");
  const recKeys = Object.keys(records?.history ?? {});
  const activeKey = recKey && recKeys.includes(recKey) ? recKey : recKeys.includes("5k") ? "5k" : recKeys[0] ?? "";
  const recPoints = useMemo(() => (records && activeKey ? records.history[activeKey] ?? [] : []), [records, activeKey]);
  const loadWeeks = weeks.filter((w) => w.load != null);
  const coverage = summary.totals.rpe_coverage;
  const intensityTotal = weeks.reduce((a, w) => a + w.intensity_seconds.low + w.intensity_seconds.moderate + w.intensity_seconds.high, 0);
  return (
    <div className="chart-grid">
      <ChartFrame
        title={t("Quilometragem por semana")}
        subtitle={t("Realizado (barras) e planejado na planilha (traço). {distance_m} no período.", { distance_m: metersToKm(summary.totals.distance_m) })}
        legend={[{ label: t("Realizado"), color: "var(--chart-1)" }, { label: t("Planejado"), color: "var(--chart-ref)", kind: "line" }]}
        table={{ head: [t("Semana"), t("Realizado"), t("Planejado"), t("Corridas")], rows: weeks.map((w) => [shortDate(w.week_start), metersToKm(w.distance_m), w.planned_distance_m ? metersToKm(w.planned_distance_m) : "—", w.sessions]) }}
        empty={weeks.every((w) => !w.distance_m && !w.planned_distance_m) ? t("Nenhuma corrida nem treino planejado no período.") : null}
      >
        <BarChart
          data={weeks.map((w) => ({
            key: w.week_start, label: shortDate(w.week_start), title: t("Semana de {week_start}", { week_start: shortDate(w.week_start) }),
            value: w.distance_m || null, ref: w.planned_distance_m || null, extra: tn("{n} corrida", "{n} corridas", w.sessions) + (w.long_run_m ? t(", maior {v}", { v: metersToKm(w.long_run_m) }) : ""),
          }))}
          fmt={(v) => `${kmNum(v)} km`}
          axisFmt={(v) => kmNum(v)}
          valueLabel={t("realizado")}
          refLabel={t("planejado")}
          labelEvery={every}
        />
      </ChartFrame>

      <ChartFrame
        title={t("Ritmo médio por corrida")}
        subtitle={bucket
          ? t("Mais rápido fica em cima; a linha é a média de 5 corridas. Só corridas de {range} km.", { range: bucket.replace("-", "–") })
          : t("Mais rápido fica em cima; a linha é a média de 5 corridas. Use o filtro de distância para comparar corridas parecidas.")}
        table={{ head: [t("Data"), t("Corrida"), t("Distância"), t("Ritmo")], rows: paced.map((p) => [formatDate(p.date), p.name, metersToKm(p.distance_m), formatPace(p.pace_s_per_km)]) }}
        empty={paced.length === 0 ? t("Sem corridas com distância e tempo no filtro escolhido.") : null}
      >
        <TimeChart
          points={paced.map((p) => ({ key: p.activity_id, date: p.date, value: p.pace_s_per_km!, title: `${formatDate(p.date)} · ${p.name}`, extra: [metersToKm(p.distance_m), ...(p.avg_hr ? [`${p.avg_hr} bpm`] : [])] }))}
          fmt={paceShort}
          valueLabel="min/km"
          invert
          trend
          from={period.start}
          to={period.end}
        />
      </ChartFrame>

      <ChartFrame
        title={t("Carga de treino semanal (sRPE)")}
        subtitle={coverage != null
          ? t("RPE × duração em minutos, em unidades arbitrárias (UA). {pct} % das corridas do período têm RPE. Semanas sem RPE ficam vazias, não zeradas.", { pct: Math.round(coverage * 100) })
          : t("RPE × duração em minutos, em unidades arbitrárias (UA). Semanas sem RPE ficam vazias, não zeradas.")}
        table={{ head: [t("Semana"), t("Carga (UA)"), t("Sessões com RPE"), t("Monotonia")], rows: weeks.map((w) => [shortDate(w.week_start), w.load != null ? Math.round(w.load) : "—", t("{a} de {b}", { a: w.load_sessions, b: w.all_sessions }), w.monotony ?? "—"]) }}
        empty={loadWeeks.length === 0 ? t("Nenhuma sessão com esforço percebido (RPE) no período.") : null}
      >
        <BarChart
          data={weeks.map((w) => ({
            key: w.week_start, label: shortDate(w.week_start), title: t("Semana de {week_start}", { week_start: shortDate(w.week_start) }), value: w.load,
            extra: w.load_estimated
              ? t("RPE em {a} de {b} sessões ({est} pela duração planejada)", { a: w.load_sessions, b: w.all_sessions, est: w.load_estimated })
              : t("RPE em {a} de {b} sessões", { a: w.load_sessions, b: w.all_sessions }),
          }))}
          fmt={(v) => `${Math.round(v)} UA`}
          axisFmt={(v) => String(Math.round(v))}
          valueLabel={t("carga")}
          labelEvery={every}
        />
      </ChartFrame>

      <ChartFrame
        title={t("Distribuição de intensidade")}
        subtitle={t("Minutos em intensidade baixa, moderada e alta pela FC (limites de 82 % e 88 % da FC máxima).")}
        legend={(["low", "moderate", "high"] as const).map((k) => ({ label: INTENSITY_LABEL[k], color: INTENSITY_COLOR[k] }))}
        table={{ head: [t("Semana"), t("Baixa"), t("Moderada"), t("Alta")], rows: weeks.map((w) => [shortDate(w.week_start), minutes(w.intensity_seconds.low), minutes(w.intensity_seconds.moderate), minutes(w.intensity_seconds.high)]) }}
        empty={intensityTotal === 0 ? t("Sem dados de FC ou sem FC no perfil. Declare a FC máxima e as faixas de intensidade em Perfil → Frequência cardíaca e faixas de intensidade (o treinador também pode informar no perfil esportivo).") : null}
      >
        <StackedBars
          data={weeks.map((w) => ({
            key: w.week_start, label: shortDate(w.week_start), title: t("Semana de {week_start}", { week_start: shortDate(w.week_start) }),
            parts: (["low", "moderate", "high"] as const).map((k) => ({ key: k, label: INTENSITY_LABEL[k], value: w.intensity_seconds[k], color: INTENSITY_COLOR[k] })),
          }))}
          fmt={(v) => minutes(v)}
          labelEvery={every}
        />
      </ChartFrame>

      <ChartFrame
        title={t("Eficiência: metros por batimento")}
        subtitle={t("Velocidade ÷ FC média. Subir indica correr mais rápido com a mesma FC. Terreno, calor e tipo de treino influenciam — compare corridas parecidas.")}
        table={{ head: [t("Data"), t("Corrida"), "m/batimento", t("FC média")], rows: eff.map((p) => [formatDate(p.date), p.name, p.efficiency!.toLocaleString(LOCALE), p.avg_hr ?? "—"]) }}
        empty={eff.length === 0 ? t("Sem corridas com FC no filtro escolhido.") : null}
      >
        <TimeChart
          points={eff.map((p) => ({ key: p.activity_id, date: p.date, value: p.efficiency!, title: `${formatDate(p.date)} · ${p.name}`, extra: [`${p.avg_hr} bpm`, formatPace(p.pace_s_per_km)] }))}
          fmt={(v) => v.toLocaleString(LOCALE, { maximumFractionDigits: 2 })}
          valueLabel={t("m/batimento")}
          trend
          from={period.start}
          to={period.end}
        />
      </ChartFrame>

      <ChartFrame
        title={t("Duração por sessão")}
        subtitle={t("Tempo em movimento de cada corrida do período.")}
        table={{ head: [t("Data"), t("Corrida"), t("Tempo")], rows: runs.map((p) => [formatDate(p.date), p.name, formatDuration(p.moving_time_s)]) }}
        empty={runs.length === 0 ? t("Sem corridas no período.") : null}
      >
        <TimeChart
          points={runs.filter((p) => p.moving_time_s).map((p) => ({ key: p.activity_id, date: p.date, value: p.moving_time_s! / 60, title: `${formatDate(p.date)} · ${p.name}`, extra: [metersToKm(p.distance_m)] }))}
          fmt={(v) => `${Math.round(v)} min`}
          valueLabel={t("minutos")}
          from={period.start}
          to={period.end}
        />
      </ChartFrame>

      <ChartFrame
        title={t("Evolução das marcas pessoais")}
        subtitle={
          recKeys.length > 1 ? (
            <span className="segmented" style={{ marginTop: 6 }}>
              {recKeys.map((k) => (
                <button key={k} type="button" aria-pressed={k === activeKey} onClick={() => setRecKey(k)}>
                  {records!.history[k][0]?.label ?? k}
                </button>
              ))}
            </span>
          ) : (
            t("Cada ponto é uma nova melhor marca.")
          )
        }
        table={{ head: [t("Data"), t("Tempo"), t("Ritmo"), t("Fonte")], rows: recPoints.map((r) => [formatDate(r.achieved_on), formatDuration(r.time_s), formatPace(r.pace_s_per_km), SOURCE_LABEL[r.source]]) }}
        empty={recPoints.length === 0 ? t("Ainda sem marcas registradas.") : null}
      >
        <TimeChart
          points={recPoints.map((r, i) => ({ key: `${r.achieved_on}${i}`, date: r.achieved_on, value: r.time_s, title: `${r.label} · ${formatDate(r.achieved_on)}`, extra: [formatPace(r.pace_s_per_km), SOURCE_LABEL[r.source]] }))}
          fmt={(v) => formatDuration(v)}
          valueLabel={t("tempo")}
          invert
          step
          from={recPoints[0]?.achieved_on && recPoints[0].achieved_on < period.start ? recPoints[0].achieved_on : period.start}
          to={period.end}
        />
      </ChartFrame>

      <ChartFrame
        title={t("Volume por mês")}
        subtitle={t("Quilometragem de corrida por mês civil dentro do período.")}
        table={{ head: [t("Mês"), t("Distância"), t("Corridas"), t("Tempo")], rows: summary.months.map((m) => [m.month.split("-").reverse().join("/"), metersToKm(m.distance_m), m.sessions, formatDuration(m.moving_time_s)]) }}
        empty={summary.months.length === 0 ? t("Sem corridas no período.") : null}
      >
        <BarChart
          data={summary.months.map((m) => ({ key: m.month, label: m.month.split("-").reverse().join("/"), title: m.month.split("-").reverse().join("/"), value: m.distance_m || null, extra: tn("{n} corrida", "{n} corridas", m.sessions) }))}
          fmt={(v) => `${kmNum(v)} km`}
          axisFmt={(v) => kmNum(v)}
          valueLabel={t("distância")}
        />
      </ChartFrame>
    </div>
  );
}
